/**
 * On-demand loading of the global Mapbox libraries.
 *
 * mapbox-gl / mapbox-gl-language / mapbox-sdk are NOT on the page by default:
 * `public/index.html` only defines `window.loadMapbox()`, which injects the
 * three scripts plus mapbox-gl.css on first call and resolves once they are
 * ready. Nothing Mapbox-related is fetched until some feature asks for it.
 *
 * Everything in the app goes through this module:
 *  - `loadMapsLib()`   — start (or join) the load, fire-and-forget friendly.
 *  - `waitForMapsLib()`— start the load and await `window.mapboxgl`.
 *  - `useMapsLibLoaded(enabled)` — render gate; only loads while `enabled`.
 *
 * Because loading is now triggered by the caller, WHERE these are called is a
 * performance decision: calling any of them mounts 260 KiB of JS. Gate them on
 * "the user is about to need a map" (map container near the viewport, search
 * field focused), never on plain page load.
 */
import { useEffect, useState } from 'react';

import config from '../config';

export const isMapsLibLoaded = () => typeof window !== 'undefined' && !!window.mapboxgl;

const ensureAccessToken = (mapboxgl) => {
  if (mapboxgl && !mapboxgl.accessToken) {
    mapboxgl.accessToken = config.maps.mapboxAccessToken;
  }
  return mapboxgl || null;
};

/**
 * Triggers the Mapbox download if it has not started yet. Idempotent — the
 * loader in index.html hands out the same promise to every caller.
 *
 * Use this to warm the libraries up ahead of an interaction that will need
 * them; use `waitForMapsLib` when you actually need the handle.
 *
 * @returns {Promise<Object|null>} resolves with `window.mapboxgl`, or `null`
 * when Mapbox is unavailable (SSR, load failure, test environment).
 */
export const loadMapsLib = () => {
  if (typeof window === 'undefined') return Promise.resolve(null);
  if (isMapsLibLoaded()) return Promise.resolve(ensureAccessToken(window.mapboxgl));
  if (typeof window.loadMapbox === 'function') {
    return window.loadMapbox().then(ensureAccessToken);
  }
  // No loader on the page (tests, storybook, legacy HTML): fall back to the
  // readiness promise if one exists, otherwise report what is there.
  if (window.mapboxReady && typeof window.mapboxReady.then === 'function') {
    return window.mapboxReady.then(ensureAccessToken);
  }
  return Promise.resolve(ensureAccessToken(window.mapboxgl));
};

/**
 * Resolves with `window.mapboxgl` (access token guaranteed to be set) once the
 * Mapbox libraries are loaded, or with `null` when they could not be loaded —
 * callers keep whatever fallback UI they render.
 *
 * NOTE: calling this STARTS the download. It is not a passive observer.
 *
 * @returns {Promise<Object|null>}
 */
export const waitForMapsLib = () => loadMapsLib();

/**
 * Reactive render gate: starts the Mapbox load on mount and re-renders the
 * component once the libraries are available.
 *
 * Deliberately starts as `false` even when the lib is already present: the
 * server-rendered HTML was produced with `false`, so the first client render
 * must match it (no hydration mismatch). The flip happens in the mount effect.
 *
 * @param {boolean} [enabled=true] when false, nothing is loaded and the hook
 * stays `false`. Lets a component that is mounted on every page (e.g.
 * MapProvider) defer the download until a map is actually claimed.
 * @returns {boolean}
 */
export const useMapsLibLoaded = (enabled = true) => {
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    loadMapsLib().then((mapboxgl) => {
      if (!cancelled && mapboxgl) setLoaded(true);
    });
    return () => {
      cancelled = true;
    };
  }, [enabled]);
  return loaded;
};
